import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { API } from "../lib/api";

// /best directory — lists every non-empty "best X tools" hub, grouped by kind.
// A central internal-linking hub that funnels crawl equity to the programmatic
// /best/<slug> pages.
const KIND_LABELS = {
  asset: "by asset class",
  category: "by function",
  assetfn: "by asset + function",
  instrument: "by instrument",
  free: "free tools",
  strategy: "by strategy / use case",
  concept: "by use case",
  tag: "by topic",
};
const KIND_ORDER = ["asset", "category", "assetfn", "instrument", "free", "strategy", "concept", "tag"];

export default function BestIndex() {
  const [hubs, setHubs] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | error

  useEffect(() => {
    let alive = true;
    Promise.resolve().then(() => {
      if (!alive) return;
      setStatus("loading");
      axios
        .get(`${API}/best`)
        .then((r) => {
          if (!alive) return;
          setHubs(r.data?.hubs || []);
          setStatus("ok");
        })
        .catch(() => {
          if (!alive) return;
          setStatus("error");
        });
    });
    return () => {
      alive = false;
    };
  }, []);

  const grouped = {};
  (hubs || []).forEach((h) => {
    (grouped[h.kind] = grouped[h.kind] || []).push(h);
  });

  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Best investing tools, by category",
    itemListElement: (hubs || []).map((h, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: h.title,
      url: `https://terminalvalue.io/best/${h.slug}`,
    })),
  };

  return (
    <div data-testid="best-index">
      <Seo
        title="Best investing tools — curated lists"
        description="Browse the best investing tools by function, asset class and topic — ranked by upvotes."
        path="/best"
        jsonLd={status === "ok" ? itemListLd : undefined}
      />
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="text-[12px] text-[#888] mb-3">
          <Link to="/" className="tv-link">index</Link>{" / "}
          <span className="text-black">best</span>
        </div>

        <h1 className="text-xl font-bold border-b border-black pb-2" data-testid="best-index-title">
          {">"}_ best investing tools
        </h1>
        <p className="mt-4 text-[14px] text-[#222] leading-relaxed">
          Curated &ldquo;best of&rdquo; lists, ranked by upvotes. Pick a function,
          asset class or topic.
        </p>

        {status === "loading" && (
          <p className="mt-6 text-[13px] text-[#888]">
            loading lists...<span className="tv-blink">_</span>
          </p>
        )}
        {status === "error" && (
          <p className="mt-6 text-[13px] text-[#444]">could not load. try again.</p>
        )}

        {status === "ok" &&
          KIND_ORDER.filter((k) => grouped[k]?.length).map((kind) => (
            <section key={kind} className="mt-8" data-testid={`best-group-${kind}`}>
              <div className="text-[11px] uppercase tracking-wide text-[#888] mb-2 border-b border-[#eee] pb-1">
                {KIND_LABELS[kind]}
              </div>
              <ul className="list-none p-0 m-0 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-[14px]">
                {grouped[kind].map((h) => (
                  <li key={h.slug} className="flex items-baseline justify-between gap-2 border-b border-[#f3f3f3] py-0.5">
                    <Link to={`/best/${h.slug}`} className="tv-link" data-testid={`best-hub-${h.slug}`}>
                      best {h.label}
                    </Link>
                    <span className="text-[11px] text-[#bbb] tabular-nums shrink-0">{h.count}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}

        {status === "ok" && (hubs || []).length === 0 && (
          <p className="mt-6 text-[13px] text-[#666]">No lists yet.</p>
        )}

        <div className="mt-10">
          <Link to="/" className="tv-btn" data-testid="best-index-back">[← back to index]</Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
